/**
 * Voice recording upload helper.
 *
 * Two-step pipeline so the audio binary never traverses the BFF:
 *
 *   1. POST /api/chat/v1/media/upload-url with the audio metadata
 *      → returns a paired { putUrl, getUrl, mediaKey } from S3.
 *   2. PUT the blob directly to the presigned `putUrl` with the same
 *      `Content-Type` we declared in step 1 (S3 enforces this).
 *
 * The caller then sends a regular chat message that includes the GET URL
 * as `audioUrl` so n8n can fetch the audio over HTTPS to transcribe.
 * `mediaKey` is returned for analytics/log correlation only — it does not
 * travel over the wire to the BFF or to n8n.
 */

export type VoiceUploadResult = {
  mediaKey: string;
  downloadUrl: string;
  contentType: string;
};

export type VoiceUploadErrorReason =
  | 'mint_url_failed'
  | 'unsupported_content_type'
  | 'too_large'
  | 's3_put_failed'
  | 'network_error';

export class VoiceUploadError extends Error {
  reason: VoiceUploadErrorReason;
  status?: number;

  constructor(reason: VoiceUploadErrorReason, message: string, status?: number) {
    super(message);
    this.name = 'VoiceUploadError';
    this.reason = reason;
    this.status = status;
  }
}

interface MintUrlResponse {
  mediaKey: string;
  putUrl: string;
  getUrl: string;
  expiresIn: number;
}

interface UploadVoiceRecordingOptions {
  /** Origin of the BFF (e.g. https://chat.example.com). */
  apiEndpoint: string;
  /** The captured audio. Its `type` is forwarded to the BFF. */
  blob: Blob;
  /** Optional fetch override for testing. */
  fetchImpl?: typeof fetch;
}

/**
 * Uploads `blob` to S3 via the BFF presigned URL endpoint. Throws a
 * `VoiceUploadError` with a structured `reason` so the caller can show the
 * correct WidgetNotice variant ("Microphone blocked" stays exclusive to
 * mic capture failures; this helper only reports network/upload errors).
 */
export async function uploadVoiceRecording({
  apiEndpoint,
  blob,
  fetchImpl,
}: UploadVoiceRecordingOptions): Promise<VoiceUploadResult> {
  const doFetch = fetchImpl ?? fetch;

  // The BFF validates `contentType` against an allowlist and `contentLength`
  // against a hard cap. We pass the blob's reported MIME and size; if the
  // browser left `type` empty we fall back to a permissible default the
  // BFF will accept.
  const contentType = blob.type && blob.type.length > 0 ? blob.type.split(';')[0].trim() : 'audio/webm';
  const contentLength = blob.size;

  let mint: MintUrlResponse;
  try {
    const mintResponse = await doFetch(`${apiEndpoint}/api/chat/v1/media/upload-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contentType, contentLength }),
    });

    if (!mintResponse.ok) {
      let reason: VoiceUploadErrorReason = 'mint_url_failed';
      try {
        const body = (await mintResponse.json()) as { error?: string };
        const msg = body?.error?.toLowerCase() ?? '';
        if (mintResponse.status === 413 || msg.includes('too large')) {
          reason = 'too_large';
        } else if (mintResponse.status === 415 || msg.includes('content type')) {
          reason = 'unsupported_content_type';
        }
      } catch {
        // Ignore body parse failure - fall through to default reason.
      }
      throw new VoiceUploadError(
        reason,
        `Failed to mint upload URL (status ${mintResponse.status}).`,
        mintResponse.status,
      );
    }

    mint = (await mintResponse.json()) as MintUrlResponse;
  } catch (err) {
    if (err instanceof VoiceUploadError) throw err;
    throw new VoiceUploadError('network_error', 'Network error while requesting upload URL.');
  }

  // Step 2: PUT the audio binary straight to S3. We MUST send the same
  // Content-Type declared in step 1 or S3 will refuse with a SignatureDoesNotMatch.
  try {
    const putResponse = await doFetch(mint.putUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: blob,
    });

    if (!putResponse.ok) {
      throw new VoiceUploadError(
        's3_put_failed',
        `S3 rejected the upload (status ${putResponse.status}).`,
        putResponse.status,
      );
    }
  } catch (err) {
    if (err instanceof VoiceUploadError) throw err;
    throw new VoiceUploadError('network_error', 'Network error while uploading audio to S3.');
  }

  return {
    mediaKey: mint.mediaKey,
    downloadUrl: mint.getUrl,
    contentType,
  };
}
